import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { supabase } from '@/lib/supabase';
import { useMedias } from '@/hooks/useMedias';

interface Screen {
  id: string;
  name: string;
}

interface PromoModalProps {
  open: boolean;
  onClose: () => void;
  screens: Screen[];
  initialData?: Record<string, unknown>;
  onSave?: (data: Record<string, unknown>) => void;
  userId?: string;
}

const PromoModal = ({ open, onClose, screens = [], initialData, onSave, userId }: PromoModalProps) => {
  // Estado para tipo de exibição do modal
  // "all" = todas as mídias, "specific" = mídias específicas, "fixed" = fixo
  const [modalType, setModalType] = useState<'all' | 'specific' | 'fixed'>(
    initialData?.['Modal visível'] === 'PERMANENT' ? 'fixed'
      : initialData?.['Modal visível'] === 'LINKED' ? 'specific'
        : 'all'
  );
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(userId || null);
  // Buscar mídias reais do banco
  const { medias, loading, refetch } = useMedias(resolvedUserId || userId);
  const [selectedMedias, setSelectedMedias] = useState<string[]>(initialData?.selected_media_ids || []);
  const [name, setName] = useState(initialData?.nome || "");
  const [goal, setGoal] = useState(initialData?.objetivo || "");
  const [quickPhrase, setQuickPhrase] = useState(initialData?.title || initialData?.quickPhrase || "");
  const [url, setUrl] = useState(initialData?.url || "");
  const [selectedScreens, setSelectedScreens] = useState<string[]>(Array.isArray(initialData?.screen_ids) ? initialData.screen_ids.map(String) : []);
  const [startDate, setStartDate] = useState(initialData?.start_date || "");
  const [endDate, setEndDate] = useState(initialData?.end_date || "");
  const [startTime, setStartTime] = useState(initialData?.start_time || "");
  const [endTime, setEndTime] = useState(initialData?.end_time || "");
  const [duration, setDuration] = useState(initialData?.duration ?? 30);
  const [interval, setInterval] = useState(initialData?.interval ?? 30);
  const [typepromo, setTypepromo] = useState(initialData?.Typepromo || "QR code");
  const [campaignId, setCampaignId] = useState(initialData?.campaign_id || "");
  const [captureChannel, setCaptureChannel] = useState(initialData?.capture_channel || "WhatsApp");
  const mapSolicitarToChannel = (v?: string) => {
    const s = (v || '').toLowerCase();
    if (s === 'ambos') return 'WhatsApp e E-mail';
    if (s === 'whatsapp') return 'WhatsApp';
    if (s === 'e-mail' || s === 'email') return 'E-mail';
    return 'WhatsApp';
  };

  useEffect(() => {
    if (initialData) {
      console.log('[PROMO MODAL] Dados iniciais recebidos:', initialData);
      console.log('[PROMO MODAL] Status do modal:', initialData['Modal visível']);
      console.log('[PROMO MODAL] Nome inicial:', initialData.nome);
      
      setName(initialData.nome || "");
      setGoal(initialData.objetivo || "");
      setQuickPhrase(initialData.title || initialData.quickPhrase || "");
      setUrl(initialData.url || "");
      setStartDate(initialData.start_date || initialData.startDate || "");
      setEndDate(initialData.end_date || initialData.endDate || "");
      setStartTime(initialData.start_time || initialData.startTime || "");
      setEndTime(initialData.end_time || initialData.endTime || "");
      setDuration(
        initialData.duration !== undefined && initialData.duration !== null
          ? Number(initialData.duration)
          : 30
      );
      setInterval(
        initialData.interval !== undefined && initialData.interval !== null
          ? Number(initialData.interval)
          : 30
      );
      setSelectedScreens(Array.isArray(initialData.screen_ids) ? initialData.screen_ids.map(String) : []);
      setTypepromo(initialData.Typepromo || "QR code");
      setCampaignId(initialData.campaign_id || "");
      const solicitarValue = (initialData as Record<string, unknown>)['Solicitar'] as string | undefined;
      const channel = initialData.capture_channel || mapSolicitarToChannel(solicitarValue);
      setCaptureChannel(channel);
    } else {
      setName("");
      setGoal("");
      setQuickPhrase("");
      setUrl("");
      setSelectedScreens([]);
      setStartDate("");
      setEndDate("");
      setStartTime("");
      setEndTime("");
      setDuration(30);
      setInterval(30);
      setTypepromo("QR code");
      setCampaignId("");
      setCaptureChannel("WhatsApp");
    }
  }, [initialData, open, screens]);

  useEffect(() => {
    if (!resolvedUserId) {
      supabase.auth.getUser().then(r => {
        const uid = r.data.user?.id || null;
        if (uid) setResolvedUserId(uid);
      });
    }
  }, [resolvedUserId]);

  // Garante que ao abrir o modal de edição, as telas selecionadas sejam sempre sincronizadas
  useEffect(() => {
    if (open && initialData && Array.isArray(initialData.screen_ids)) {
      console.log('[PROMO MODAL] Configurando telas selecionadas:', initialData.screen_ids);
      setSelectedScreens(initialData.screen_ids.map(String));
    }
  }, [open, initialData]);

  // Recarregar mídias ao selecionar "mídias específicas"
  useEffect(() => {
    if (open && modalType === 'specific' && (resolvedUserId || userId)) {
      refetch?.();
    }
  }, [open, modalType, resolvedUserId, userId, refetch]);

  // Debug: Log das telas disponíveis e selecionadas
  useEffect(() => {
    if (open) {
      console.log('[PROMO MODAL] Telas disponíveis:', screens);
      console.log('[PROMO MODAL] Telas selecionadas:', selectedScreens);
    }
  }, [open, screens, selectedScreens]);

  // Função para verificar se o campo deve estar em modo de leitura
  const isNameFieldReadOnly = () => {
    // Por padrão, o campo nome é sempre editável
    // Mas pode ser customizado baseado em regras de negócio futuras
    return false;
  };

  // Função para obter o status atual do modal em formato legível
  const getCurrentStatusLabel = () => {
    switch (modalType) {
      case 'fixed':
        return 'PERMANENT';
      case 'specific':
        return 'LINKED';
      default:
        return 'TEMPORARY';
    }
  };

  const handleSave = async () => {
    // Validação do campo Nome - obrigatório para todos os status
    if (!name || name.trim().length === 0) {
      alert('O campo "Nome da promoção" é obrigatório.');
      return;
    }
    
    // Validação de comprimento do nome
    if (name.length > 100) {
      alert('O nome da promoção não pode exceder 100 caracteres.');
      return;
    }
    
    // Garante que quickPhrase sempre tenha valor (usa name como fallback)
    const promoTitle = quickPhrase || name;
    // Garante que duration e interval são números válidos
    const durationValue = Number(duration);
    const intervalValue = Number(interval);
    // Garante que screen_ids é array de string e reflete exatamente as checkboxes de telas
    const screenIds = Array.isArray(selectedScreens) ? selectedScreens.filter(Boolean).map(String) : [];
    const requireTiming = modalType !== 'fixed';
    if (!goal || !url || !startDate || !endDate || !startTime || !endTime || (!durationValue && requireTiming) || (!intervalValue && requireTiming) || screenIds.length === 0 || !promoTitle) {
      alert('Preencha todos os campos obrigatórios.');
      return;
    }
    // Determinar status para o banco
    let modalStatus = 'TEMPORARY';
    let mediaIdsToSave: string[] | null = null;
    if (modalType === 'fixed') {
      modalStatus = 'PERMANENT';
    } else if (modalType === 'specific') {
      modalStatus = 'LINKED';
      mediaIdsToSave = selectedMedias;
    } else {
      modalStatus = 'TEMPORARY';
    }
          
    // Mapear o canal de captação para o valor correto da coluna Solicitar
    let solicitarValue = '';
    if (typepromo === 'Captação de Lead') {
      if (captureChannel === 'WhatsApp') {
        solicitarValue = 'whatsapp';
      } else if (captureChannel === 'E-mail') {
        solicitarValue = 'e-mail';
      } else if (captureChannel === 'WhatsApp e E-mail') {
        solicitarValue = 'ambos';
      }
    }

    // Montar payload de UI (para onSave em PromoSection)
    const payload = {
      id: initialData?.id,
      quickPhrase: promoTitle,
      name: name,
      goal: goal,
      url,
      startDate: startDate,
      endDate: endDate,
      startTime: startTime,
      endTime: endTime,
      duration: durationValue,
      selectedScreens: screenIds,
      interval: intervalValue,
      Typepromo: typepromo,
      'Modal visível': modalStatus,
      selected_media_ids: mediaIdsToSave,
      campaign_id: campaignId || null,
      Solicitar: solicitarValue || null
    };

    // Se tiver onSave (passado pelo PromoSection), usa ele
    // Isso permite que o PromoSection gerencie o UPDATE ou INSERT
    if (onSave) {
      await onSave(payload);
      onClose();
      return;
    }

    // Caso contrário, faz INSERT direto (criação nova) apenas com colunas válidas do banco
    if (!resolvedUserId) {
      alert('Usuário não identificado. Entre novamente e tente criar a promoção.');
      return;
    }
    const dbPayload = {
      title: promoTitle,
      nome: name,
      objetivo: goal,
      url,
      start_date: startDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,
      duration: durationValue,
      screen_ids: screenIds,
      active: true,
      interval: intervalValue,
      Typepromo: typepromo,
      'Modal visível': modalStatus,
      selected_media_ids: mediaIdsToSave,
      campaign_id: campaignId || null,
      Solicitar: solicitarValue || null,
      user_id: resolvedUserId
    };
    const { error } = await supabase.from('promos').insert([dbPayload]);
    if (error) {
      alert('Erro ao salvar promoção: ' + error.message);
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={open => !open ? onClose() : undefined}>
      <DialogContent hideClose className="max-h-[80vh] sm:max-h-[60vh] overflow-y-auto p-6 promo-modal-scrollbar sm:rounded-xl" aria-describedby="promo-dialog-description">
        <DialogHeader>
          <DialogTitle className="mt-4">{initialData ? 'Editar Promoção' : 'Criar Promoção'}</DialogTitle>
          <DialogDescription id="promo-dialog-description">Configuração de exibição de promoção, datas, horários, duração e intervalo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 sm:space-y-6">
          {/* Campo Nome - SEMPRE visível em primeiro lugar */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Nome da promoção
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500 ${
                isNameFieldReadOnly() ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              value={name}
              onChange={e => !isNameFieldReadOnly() && setName(e.target.value)}
              placeholder="Digite o nome da promoção"
              maxLength={100}
              required
              readOnly={isNameFieldReadOnly()}
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">
                {name.length}/100 caracteres
              </span>
            </div>
          </div>
          {/* Seletor de tipo de exibição do modal */}
          <div className="mb-2">
            <label className="block text-sm font-medium mb-1">Como deseja exibir o modal?</label>
            <select
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
              value={modalType}
              onChange={e => setModalType(e.target.value as 'all' | 'specific' | 'fixed')}
            >
              <option value="all">Exibir em todas as mídias</option>
              <option value="specific">Exibir em mídias específicas</option>
              <option value="fixed">Exibir fixo (sempre visível)</option>
            </select>
          </div>
          {/* Se "mídias específicas" estiver selecionado, exibe checkboxes de mídias */}
          {modalType === 'specific' && (
            <div className="mb-2">
              <label className="block text-sm font-medium mb-1">Selecione as mídias para exibir o modal:</label>
              <div
                className={`border rounded p-3 bg-gray-50 ${medias.length > 4 ? 'max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-green-500 scrollbar-track-gray-200 hover:scrollbar-thumb-green-600 transition-all duration-200' : ''}`}
                style={medias.length > 4 ? { scrollbarWidth: 'thin' } : {}}
              >
                {loading ? (
                  <span className="text-xs text-gray-400">Carregando mídias...</span>
                ) : medias.length > 0 ? (
                  <div className="grid grid-cols-1 gap-2">
                    {medias.map(media => (
                      <label key={media.id} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-green-100 transition">
                        <input
                          type="checkbox"
                          checked={selectedMedias.includes(media.id)}
                          onChange={e => {
                            setSelectedMedias(prev =>
                              e.target.checked
                                ? [...prev, media.id]
                                : prev.filter(id => id !== media.id)
                            );
                          }}
                          className="accent-green-600"
                        />
                        <span className="text-sm text-gray-700">{media.title}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">Nenhuma mídia encontrada.</span>
                )}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Objetivo</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
              value={goal}
              onChange={e => setGoal(e.target.value)}
              placeholder="Breve objetivo da promoção"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo de Promoção</label>
            <select
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
              value={typepromo}
              onChange={e => setTypepromo(e.target.value)}
            >
              <option value="QR code">QR code</option>
              <option value="Captação de Lead">Captação de Lead</option>
            </select>
          </div>
          {/* Dropdown de canal de captação - aparece apenas para Captação de Lead */}
          {typepromo === "Captação de Lead" && (
            <div>
              <label className="block text-sm font-medium mb-1">Canal de Captação</label>
              <select
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
                value={captureChannel}
                onChange={e => setCaptureChannel(e.target.value)}
              >
                <option value="WhatsApp">WhatsApp</option>
                <option value="E-mail">E-mail</option>
                <option value="WhatsApp e E-mail">WhatsApp e E-mail</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Frase promocional rápida</label>
            <select
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
              value={quickPhrase}
              onChange={e => setQuickPhrase(e.target.value)}
            >
              <option value="">Selecione uma frase...</option>
              {typepromo === "Captação de Lead" ? (
                <>
                  <option value="Pegue seu cupom!">Pegue seu cupom!</option>
                  <option value="Ganhe seu cupom!">Ganhe seu cupom!</option>
                  <option value="Garanta seu cupom!">Garanta seu cupom!</option>
                </>
              ) : (
                <>
                  <option value="Novidade Imperdível!">Novidade Imperdível!</option>
                  <option value="Última Chance!">Última Chance!</option>
                  <option value="Oferta Exclusiva!">Oferta Exclusiva!</option>
                  <option value="Pegue seu desconto!">Pegue seu desconto!</option>
                  <option value="Garanta seu desconto!">Garanta seu desconto!</option>
                  <option value="Ganhe seu cupom!">Ganhe seu cupom!</option>
                  <option value="Veja Aqui!">Veja Aqui!</option>
                </>
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">URL</label>
            <input
              type="url"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://sualoja.com/promo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ID da Campanha (opcional)</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
              value={campaignId}
              onChange={e => setCampaignId(e.target.value)}
              placeholder="Ex.: PROMO50% CAPTAÇÃO LEADS"
            />
            <p className="text-xs text-green-600 mt-1">ID para rastreamento - caso não seja informado, será gerado automaticamente um identificador único.</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Selecione as telas para exibir a promoção</label>
            <div
              className={`border rounded p-3 bg-gray-50 ${screens.length > 4 ? 'max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-green-500 scrollbar-track-gray-200 hover:scrollbar-thumb-green-600 transition-all duration-200' : ''}`}
              style={screens.length > 4 ? { scrollbarWidth: 'thin' } : {}}
            >
              {(!screens || screens.length === 0) ? (
                <span className="text-xs text-gray-400">Carregando telas...</span>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(screens || []).map(screen => (
                    <label key={screen.id} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-green-100 transition">
                      <input
                        type="checkbox"
                        checked={selectedScreens.includes(screen.id)}
                        onChange={e => {
                          setSelectedScreens(prev =>
                            e.target.checked
                              ? [...prev, screen.id]
                              : prev.filter(id => id !== screen.id)
                          );
                        }}
                        className="accent-green-600"
                      />
                      <span className="text-sm text-gray-700">{screen.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Data de início</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Data de fim</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hora de início</label>
              <input
                type="time"
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hora de fim</label>
              <input
                type="time"
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Duração do modal (segundos)</label>
              <input
                type="number"
                min={10}
                step={10}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                value={duration}
                onChange={e => setDuration(Number(e.target.value))}
                disabled={modalType === 'fixed'}
                placeholder="Ex: 30, 60, 90..."
              />
              {modalType === 'fixed' && (
                <p className="text-xs text-gray-400 mt-1">Ignorado quando a promoção for fixa no player da tela.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Intervalo para reaparecer (minutos)</label>
              <input
                type="number"
                min={1}
                step={1}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:border-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                value={interval}
                onChange={e => setInterval(Number(e.target.value))}
                disabled={modalType === 'fixed'}
                placeholder="Ex: 15, 30, 60..."
              />
              {modalType === 'fixed' && (
                <p className="text-xs text-gray-400 mt-1">Ignorado quando a promoção for fixa no player da tela.</p>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="flex flex-col gap-3 mt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSave} className="mb-4 sm:mb-0">
            {initialData ? 'Editar Promoção' : 'Salvar Promoção'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromoModal;
